import React, { useState, useEffect, useMemo, useRef } from "react";
import secureLocalStorage from "react-secure-storage";
import { getData_Any2Column } from "../service/APIService";
import "./DietJourneyGraph.css";

// Grid and geometry levels for SVG chart
const Y_GRID_LEVELS = [
  { val: 800, y: 20 },
  { val: 600, y: 60 },
  { val: 400, y: 100 },
  { val: 200, y: 140 },
  { val: 0, y: 180 },
];

const TOTAL_DAYS = 28;
const STEP_X = 52;
const START_X = 54;
const CANVAS_WIDTH = START_X + (TOTAL_DAYS - 1) * STEP_X + 48; // 1482px
const CANVAS_HEIGHT = 226;

const DAY_KEYS = ["S", "M", "T", "W", "T", "F", "S"];
const DAY_NAMES = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
];

// Helper: parse numbers from strings like "40 g", "360 kcal", etc.
const parseNumber = (val) => {
  if (typeof val === "number") return val;
  if (!val) return 0;
  const match = String(val).match(/[\d.]+/);
  return match ? parseFloat(match[0]) : 0;
};

// Generates smooth cubic bezier spline SVG path for any 7 coordinate points
const generateSplinePath = (pts) => {
  if (!pts || pts.length === 0) return "";
  let path = `M ${pts[0].x},${pts[0].y}`;
  for (let i = 0; i < pts.length - 1; i++) {
    const p0 = i > 0 ? pts[i - 1] : pts[i];
    const p1 = pts[i];
    const p2 = pts[i + 1];
    const p3 = i < pts.length - 2 ? pts[i + 2] : p2;

    const cp1x = p1.x + (p2.x - p0.x) / 6;
    const cp1y = p1.y + (p2.y - p0.y) / 6;
    const cp2x = p2.x - (p3.x - p1.x) / 6;
    const cp2y = p2.y - (p3.y - p1.y) / 6;

    path += ` C ${Math.round(cp1x)},${Math.round(cp1y)} ${Math.round(cp2x)},${Math.round(cp2y)} ${p2.x},${p2.y}`;
  }
  return path;
};

const DietJourneyGraph = ({
  title = "Track your diet journey",
  calorieHistory: propHistory,
}) => {
  const [backendHistory, setBackendHistory] = useState(null);
  const scrollContainerRef = useRef(null);

  // In 28-day span (4 weeks ending on this week's Saturday), this week starts at index 21
  const todayDayIdx = new Date().getDay(); // 0=Sun .. 6=Sat
  const initialTodayIdx = 21 + todayDayIdx;
  const [activeIdx, setActiveIdx] = useState(initialTodayIdx);

  // 1. Fetch calorie history directly if not supplied via props
  useEffect(() => {
    if (propHistory && propHistory.length > 0) return;
    try {
      const user = JSON.parse(secureLocalStorage.getItem("loggedInUser"));
      const email = user?.email;
      if (email && email !== "guest") {
        getData_Any2Column("email", email, "type", "recipe-ai-food-calorie")
          .then((data) => setBackendHistory(data || []))
          .catch((err) => {
            console.error("DietJourneyGraph fetch error:", err);
            setBackendHistory([]);
          });
      }
    } catch (e) {
      console.warn("Could not retrieve user for DietJourneyGraph:", e);
    }
  }, [propHistory]);

  const history = propHistory && propHistory.length > 0
    ? propHistory
    : (backendHistory || []);

  // 2. Aggregate history data across a 28-day (4-week) scrollable timeline
  const { weekData, dateRange, isLiveBackend } = useMemo(() => {
    const nowDate = new Date();
    const currDay = nowDate.getDay(); // 0 = Sun, 6 = Sat

    // Saturday of current week (end boundary)
    const thisSaturday = new Date(nowDate);
    thisSaturday.setDate(nowDate.getDate() + (6 - currDay));
    thisSaturday.setHours(23, 59, 59, 999);

    // Start date 27 days before Saturday (total 28 days = 4 full weeks)
    const startDate = new Date(thisSaturday);
    startDate.setDate(thisSaturday.getDate() - (TOTAL_DAYS - 1));
    startDate.setHours(0, 0, 0, 0);

    const todayLocalPrefix = `${nowDate.getFullYear()}-${String(nowDate.getMonth() + 1).padStart(2, "0")}-${String(nowDate.getDate()).padStart(2, "0")}`;

    const computedDays = [];

    for (let i = 0; i < TOTAL_DAYS; i++) {
      const d = new Date(startDate);
      d.setDate(startDate.getDate() + i);

      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, "0");
      const dayNum = String(d.getDate()).padStart(2, "0");
      const datePrefix = `${y}-${m}-${dayNum}`; // Local YYYY-MM-DD

      // Find all meal items logged on this day
      const dayMeals = (history || []).filter((item) => {
        if (!item || !item.date) return false;
        const dStr = String(item.date).trim();
        if (dStr.startsWith(datePrefix)) return true;
        try {
          const parsed = new Date(dStr);
          return (
            parsed.getFullYear() === d.getFullYear() &&
            parsed.getMonth() === d.getMonth() &&
            parsed.getDate() === d.getDate()
          );
        } catch (e) {
          return false;
        }
      });

      let dayCal = 0;
      let dayFat = 0;
      let dayCarbs = 0;
      let dayProtein = 0;
      const foodTitles = [];

      dayMeals.forEach((meal) => {
        dayCal += parseNumber(meal.calories);
        dayFat += parseNumber(meal.fat);
        dayCarbs += parseNumber(meal.carbs);
        dayProtein += parseNumber(meal.protein);
        if (meal.title && meal.title !== "Unknown" && meal.title !== "Image" && meal.title !== "❌ Not Food") {
          foodTitles.push(meal.title.trim());
        }
      });

      // Coordinate Y: scaled between 0 kcal (y=172) and 800+ kcal (y=20)
      const clampedCal = Math.min(800, Math.max(0, dayCal));
      const yCoord = dayCal > 0 ? Math.round(172 - (clampedCal / 800) * 148) : 172;

      computedDays.push({
        idx: i,
        key: DAY_KEYS[d.getDay()],
        dayName: DAY_NAMES[d.getDay()],
        date: d.toLocaleDateString("en-US", { day: "numeric", month: "short" }),
        dateNum: d.getDate(),
        fullDate: datePrefix,
        x: START_X + i * STEP_X,
        y: yCoord,
        calories: dayCal,
        fat: `${Math.round(dayFat)} g`,
        carbs: `${Math.round(dayCarbs)} g`,
        protein: `${Math.round(dayProtein)} g`,
        foodName: foodTitles.length > 0 ? foodTitles[0] : "No meals logged",
        foods: foodTitles,
        hasRealScans: dayMeals.length > 0,
        isToday: datePrefix === todayLocalPrefix,
      });
    }

    const rangeStr = `${computedDays[0].date} – ${computedDays[computedDays.length - 1].date}`;

    return {
      weekData: computedDays,
      dateRange: rangeStr,
      isLiveBackend: true,
    };
  }, [history]);

  const activeDay = weekData[activeIdx] || weekData[initialTodayIdx] || weekData[weekData.length - 1] || weekData[0];

  // Active 7-day week window (based on current active day)
  const activeWeekStart = Math.floor(activeIdx / 7) * 7;
  const activeWeekEnd = Math.min(weekData.length - 1, activeWeekStart + 6);
  const activeWeekDays = weekData.slice(activeWeekStart, activeWeekEnd + 1);
  const activeWeekKcal = activeWeekDays.reduce((acc, d) => acc + (d.calories || 0), 0);

  const startDay = activeWeekDays[0];
  const endDay = activeWeekDays[activeWeekDays.length - 1];
  const activeWeekRangeStr = startDay && endDay ? `${startDay.date} – ${endDay.date}` : dateRange;
  const isCurrentWeek = activeWeekStart === 21;

  // Week navigation jump controls (< 7 days, > 7 days)
  const handlePrevWeek = () => {
    setActiveIdx((prev) => Math.max(0, prev - 7));
  };

  const handleNextWeek = () => {
    setActiveIdx((prev) => Math.min(weekData.length - 1, prev + 7));
  };

  // On mount, auto-scroll horizontally so today's point is centered in view
  useEffect(() => {
    if (!scrollContainerRef.current) return;
    const timer = setTimeout(() => {
      if (scrollContainerRef.current && weekData[activeIdx]) {
        const activeX = weekData[activeIdx].x;
        const cWidth = scrollContainerRef.current.clientWidth || 360;
        scrollContainerRef.current.scrollLeft = Math.max(0, activeX - cWidth / 2);
      }
    }, 150);
    return () => clearTimeout(timer);
  }, []);

  // When activeIdx changes (e.g. by week buttons or day clicks), smoothly bring it into view
  useEffect(() => {
    if (!scrollContainerRef.current || !weekData[activeIdx]) return;
    const container = scrollContainerRef.current;
    const activeX = weekData[activeIdx].x;
    const viewLeft = container.scrollLeft;
    const viewRight = viewLeft + container.clientWidth;

    if (activeX < viewLeft + 65 || activeX > viewRight - 65) {
      container.scrollTo({
        left: Math.max(0, activeX - container.clientWidth / 2),
        behavior: "smooth",
      });
    }
  }, [activeIdx, weekData]);

  // Tooltip geometry calculations (clamped within canvas bounds)
  const bubbleW = 126;
  const bubbleH = 84;
  const bubbleX = Math.max(10, Math.min(CANVAS_WIDTH - bubbleW - 10, activeDay.x - bubbleW / 2));
  const bubbleY = Math.max(8, activeDay.y - bubbleH - 12);

  // Tail path connecting speech bubble to active data point
  const tailPointX = activeDay.x;
  const tailBaseLeft = Math.max(bubbleX + 12, Math.min(bubbleX + bubbleW - 28, tailPointX - 8));
  const tailBaseRight = tailBaseLeft + 16;
  const tailPath = `M ${tailBaseLeft} ${bubbleY + bubbleH - 1} 
                   L ${tailPointX} ${activeDay.y - 4} 
                   L ${tailBaseRight} ${bubbleY + bubbleH - 1} Z`;

  // Curve and area path for all 28 data points
  const splinePath = useMemo(() => generateSplinePath(weekData), [weekData]);
  const areaPath = useMemo(() => {
    if (!splinePath || weekData.length === 0) return "";
    const firstX = weekData[0].x;
    const lastX = weekData[weekData.length - 1].x;
    return `${splinePath} L ${lastX},180 L ${firstX},180 Z`;
  }, [splinePath, weekData]);

  // Clean food display name for tooltip (truncated if too long)
  const displayFoodName = useMemo(() => {
    if (!activeDay.hasRealScans) {
      return "No Meals Logged";
    }
    const raw = activeDay.foodName || "Meal";
    return raw.length > 17 ? raw.slice(0, 16) + "…" : raw;
  }, [activeDay.foodName, activeDay.hasRealScans]);

  return (
    <div className="diet-journey-card">
      {/* Header section with title, dates & food plate art */}
      <div className="diet-journey-header">
        <div className="diet-journey-title-wrap">
          <div className="d-flex align-items-center gap-2">
            <h3 className="diet-journey-title">{title}</h3>
            {isLiveBackend && (
              <span className="diet-live-badge" title="Live data from your logged meals">
                <span className="live-dot"></span> Live
              </span>
            )}
          </div>
          <div className="diet-journey-subtitle">
            <div className="diet-week-nav-controls">
              <button
                type="button"
                className="diet-week-nav-btn"
                onClick={handlePrevWeek}
                disabled={activeIdx < 7}
                title="Previous 7 Days"
                aria-label="Previous 7 Days"
              >
                ◀
              </button>
              <span className="date-span">
                {activeWeekRangeStr} {isCurrentWeek ? "(This Week)" : ""}
              </span>
              <button
                type="button"
                className="diet-week-nav-btn"
                onClick={handleNextWeek}
                disabled={activeIdx >= weekData.length - 7}
                title="Next 7 Days"
                aria-label="Next 7 Days"
              >
                ▶
              </button>
            </div>
            <span className="divider-dot">|</span>
            <span className="kcal-span">{activeWeekKcal.toLocaleString()} Kcal</span>
          </div>
        </div>

        {/* Top-Right Decorative Salmon Salad Bowl Illustration */}
        <div className="diet-journey-dish-art" aria-hidden="true">
          <svg viewBox="0 0 120 120" width="100%" height="100%" fill="none">
            {/* Ceramic Salad Bowl Outer Rim */}
            <circle cx="68" cy="52" r="46" fill="#3f2723" />
            <circle cx="68" cy="52" r="42" fill="#5d4037" />
            <circle cx="68" cy="52" r="39" fill="#1b382b" />

            {/* Fresh Salad Greens Layer */}
            <path
              d="M 38 46 Q 48 30 68 34 Q 88 28 98 44 Q 106 62 94 76 Q 78 90 54 84 Q 34 76 38 46 Z"
              fill="#2e7d32"
            />
            <path
              d="M 44 54 Q 54 38 72 40 Q 86 36 92 50 Q 98 64 88 74 Q 72 84 52 78 Q 40 70 44 54 Z"
              fill="#43a047"
            />

            {/* Cucumber Slices */}
            <circle cx="48" cy="62" r="9" fill="#a5d6a7" stroke="#388e3c" strokeWidth="2" />
            <circle cx="48" cy="62" r="4" fill="#c8e6c9" />
            <circle cx="86" cy="46" r="8" fill="#a5d6a7" stroke="#388e3c" strokeWidth="2" />
            <circle cx="86" cy="46" r="3.5" fill="#c8e6c9" />
            <circle cx="84" cy="72" r="8.5" fill="#a5d6a7" stroke="#388e3c" strokeWidth="2" />

            {/* Cherry Tomato Halves */}
            <path d="M 46 42 A 6 6 0 0 1 58 42 Z" fill="#e53935" transform="rotate(-25 52 42)" />
            <path d="M 78 36 A 6 6 0 0 1 90 36 Z" fill="#e53935" transform="rotate(35 84 36)" />
            <path d="M 62 82 A 6.5 6.5 0 0 1 75 82 Z" fill="#e53935" transform="rotate(-10 68 82)" />

            {/* Sliced Avocado Fan */}
            <path
              d="M 72 32 C 78 28 86 34 84 42 C 82 48 74 54 68 50 C 64 46 66 36 72 32 Z"
              fill="#9ccc65"
              stroke="#558b2f"
              strokeWidth="1.5"
            />
            <path
              d="M 78 36 C 84 32 90 38 88 45 C 86 50 80 56 74 52 Z"
              fill="#c5e1a5"
            />

            {/* Grilled Salmon Fillet */}
            <path
              d="M 44 52 C 48 42 62 46 76 50 C 90 54 94 66 88 74 C 80 82 66 80 52 74 C 42 68 40 58 44 52 Z"
              fill="#fb923c"
              stroke="#ea580c"
              strokeWidth="1.5"
            />
            {/* Salmon Flesh Texture Lines */}
            <path d="M 52 50 Q 56 62 58 72" stroke="#fdba74" strokeWidth="1.8" strokeLinecap="round" />
            <path d="M 62 52 Q 66 64 68 74" stroke="#fdba74" strokeWidth="1.8" strokeLinecap="round" />
            <path d="M 72 54 Q 76 66 78 74" stroke="#fdba74" strokeWidth="1.8" strokeLinecap="round" />
            <path d="M 80 58 Q 84 66 86 72" stroke="#fdba74" strokeWidth="1.8" strokeLinecap="round" />
            {/* Grill Marks */}
            <path d="M 48 58 L 56 52" stroke="#9a3412" strokeWidth="1.8" strokeLinecap="round" />
            <path d="M 58 64 L 68 56" stroke="#9a3412" strokeWidth="1.8" strokeLinecap="round" />
            <path d="M 70 68 L 80 60" stroke="#9a3412" strokeWidth="1.8" strokeLinecap="round" />

            {/* Seasoning Shaker Glass Bottle In Background */}
            <path d="M 28 14 L 38 14 L 40 28 L 26 28 Z" fill="#b0bec5" opacity="0.85" />
            <rect x="29" y="8" width="8" height="6" rx="2" fill="#78909c" />
          </svg>
        </div>
      </div>

      {/* SVG Interactive Line Chart Canvas with Horizontal Scroll */}
      <div className="diet-graph-wrapper">
        {/* Sticky Y-Axis Legend on left */}
        <div className="diet-sticky-yaxis-col" aria-hidden="true">
          {Y_GRID_LEVELS.map((lvl) => (
            <div
              key={`sticky-${lvl.val}`}
              className="diet-sticky-yaxis-val"
              style={{ top: `${lvl.y - 7}px` }}
            >
              {lvl.val}
            </div>
          ))}
        </div>

        {/* Horizontally Scrollable SVG Canvas */}
        <div
          className="diet-graph-scroll-container"
          ref={scrollContainerRef}
        >
          <svg
            className="diet-graph-svg"
            width={CANVAS_WIDTH}
            height={CANVAS_HEIGHT}
            viewBox={`0 0 ${CANVAS_WIDTH} ${CANVAS_HEIGHT}`}
          >
            <defs>
              {/* Line Stroke Multi-Color Gradient (Yellow -> Coral -> Purple) */}
              <linearGradient id="dietStrokeGrad" x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" stopColor="#f59e0b" />
                <stop offset="35%" stopColor="#f97316" />
                <stop offset="65%" stopColor="#fb7185" />
                <stop offset="85%" stopColor="#c084fc" />
                <stop offset="100%" stopColor="#8b5cf6" />
              </linearGradient>

              {/* Area Fill Gradient Underneath Curve */}
              <linearGradient id="dietAreaGrad" x1="0%" y1="0%" x2="0%" y2="100%">
                <stop offset="0%" stopColor="rgba(216, 70, 239, 0.24)" />
                <stop offset="45%" stopColor="rgba(251, 113, 133, 0.12)" />
                <stop offset="80%" stopColor="rgba(245, 158, 11, 0.04)" />
                <stop offset="100%" stopColor="rgba(255, 255, 255, 0)" />
              </linearGradient>

              {/* Yellow Tooltip Drop Shadow Filter */}
              <filter id="dietTooltipShadow" x="-20%" y="-20%" width="140%" height="140%">
                <feDropShadow dx="0" dy="5" stdDeviation="6" floodColor="#ca8a04" floodOpacity="0.28" />
              </filter>
            </defs>

            {/* Horizontal Dotted Guide Lines spanning the entire scroll width */}
            {Y_GRID_LEVELS.map((lvl) => (
              <g key={lvl.val}>
                <line
                  x1="20"
                  y1={lvl.y}
                  x2={CANVAS_WIDTH - 16}
                  y2={lvl.y}
                  stroke="#f1f5f9"
                  strokeDasharray="3, 4"
                  strokeWidth="1.2"
                />
              </g>
            ))}

            {/* Spline Area Fill (Fade In) */}
            <path
              className="diet-spline-area"
              d={areaPath}
              fill="url(#dietAreaGrad)"
            />

            {/* Spline Stroke Path (Draw Animation) */}
            <path
              className="diet-spline-curve"
              d={splinePath}
              stroke="url(#dietStrokeGrad)"
              strokeWidth="3.2"
            />

            {/* Interactive Invisible Day Vertical Hit Columns */}
            {weekData.map((day, idx) => (
              <rect
                key={`hit-${idx}`}
                x={day.x - 25}
                y="10"
                width="50"
                height="215"
                className="diet-day-hit-area"
                onClick={() => setActiveIdx(idx)}
              />
            ))}

            {/* Active Data Point Ring & Halo */}
            <circle
              cx={activeDay.x}
              cy={activeDay.y}
              r="12"
              fill="rgba(244, 63, 94, 0.22)"
              className="diet-active-pin-glow"
            />
            <circle
              cx={activeDay.x}
              cy={activeDay.y}
              r="6"
              fill="#ffffff"
              stroke="#e11d48"
              strokeWidth="2.5"
              className="diet-active-pin-ring"
            />

            {/* Floating Yellow Macro Speech Bubble Tooltip */}
            <g
              className="diet-tooltip-group"
              filter="url(#dietTooltipShadow)"
            >
              {/* Speech Bubble Card */}
              <rect
                x={bubbleX}
                y={bubbleY}
                width={bubbleW}
                height={bubbleH}
                className="diet-tooltip-bubble"
              />
              {/* Pointer Tail */}
              <path d={tailPath} className="diet-tooltip-tail" />

              {/* Food Name Header (Placed Directly Above Fat) */}
              <text
                x={bubbleX + 12}
                y={bubbleY + 18}
                className="diet-tooltip-food-title"
              >
                {displayFoodName}
              </text>
              <line
                x1={bubbleX + 10}
                y1={bubbleY + 25}
                x2={bubbleX + bubbleW - 10}
                y2={bubbleY + 25}
                className="diet-tooltip-divider"
              />

              {/* Macro Breakdown Rows */}
              {/* Fat */}
              <text x={bubbleX + 12} y={bubbleY + 41} className="diet-tooltip-label">
                Fat
              </text>
              <text x={bubbleX + bubbleW - 12} y={bubbleY + 41} className="diet-tooltip-value">
                {activeDay.fat}
              </text>

              {/* Carbs */}
              <text x={bubbleX + 12} y={bubbleY + 58} className="diet-tooltip-label">
                Carbs
              </text>
              <text x={bubbleX + bubbleW - 12} y={bubbleY + 58} className="diet-tooltip-value">
                {activeDay.carbs}
              </text>

              {/* Protein */}
              <text x={bubbleX + 12} y={bubbleY + 75} className="diet-tooltip-label">
                Protein
              </text>
              <text x={bubbleX + bubbleW - 12} y={bubbleY + 75} className="diet-tooltip-value">
                {activeDay.protein}
              </text>
            </g>

            {/* X-Axis Days of Week (S M T W T F S) with date numbers */}
            {weekData.map((day, idx) => {
              const isActive = idx === activeIdx;
              return (
                <g key={`day-${idx}`} onClick={() => setActiveIdx(idx)} style={{ cursor: "pointer" }}>
                  {isActive && (
                    <rect
                      x={day.x - 14}
                      y="188"
                      width="28"
                      height="35"
                      rx="14"
                      className="diet-day-active-pill"
                    />
                  )}
                  <text
                    x={day.x}
                    y="201"
                    textAnchor="middle"
                    className={`diet-day-btn ${isActive ? "is-active" : ""}`}
                  >
                    {day.key}
                  </text>
                  <text
                    x={day.x}
                    y="216"
                    textAnchor="middle"
                    className={`diet-day-date-num ${isActive ? "is-active" : ""}`}
                    style={{
                      fontSize: "9px",
                      fill: isActive ? "#0f1f14" : "#94a3b8",
                      fontWeight: isActive ? "800" : "500",
                    }}
                  >
                    {day.dateNum}
                  </text>
                </g>
              );
            })}
          </svg>
        </div>
      </div>

      {/* Horizontal Scroll Hint */}
      <div className="diet-scroll-hint">
        <span>⟵</span> Swipe or scroll horizontally to explore all 28 days <span>⟶</span>
      </div>

      {/* Selected Day Food Summary Strip Below Graph */}
      <div className="diet-day-summary-strip">
        <div className="diet-day-summary-tag">
          <span className="diet-day-indicator-dot"></span>
          <span className="diet-day-summary-day">{activeDay.dayName}</span>
          <span className="diet-day-summary-date">({activeDay.date})</span>
        </div>
        <div className="diet-day-summary-content">
          <span className="diet-day-food-icon">🍽️</span>
          <span className="diet-day-food-names">
            {activeDay.hasRealScans && activeDay.foods && activeDay.foods.length > 0
              ? activeDay.foods.join(" • ")
              : `No meals logged for ${activeDay.dayName}`}
          </span>
        </div>
        <div className="diet-day-summary-calories">
          <span className="cal-val">{activeDay.calories}</span>
          <span className="cal-unit">kcal</span>
        </div>
      </div>
    </div>
  );
};

export default DietJourneyGraph;
